import { LODGE_EMAIL, LODGE_PHONE_SCHEMA } from "@/lib/contact-info";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import heroVideo from "@/assets/hero-king-salmon.mp4.asset.json";
import heroPoster from "@/assets/hero-king-salmon-poster.jpg.asset.json";
import lodgeImg from "@/assets/lodge-porch-v2.png.asset.json";
import kingImg from "@/assets/halibut-catch.jpeg.asset.json";
import kayakImg from "@/assets/rentals-hewescraft-1100.jpg.asset.json";
import kayakImg700 from "@/assets/rentals-hewescraft-700.jpg.asset.json";
import kayakImg1600 from "@/assets/rentals-hewescraft-1600.jpg.asset.json";
import { Compass, Fish, Waves } from "lucide-react";

import { seoHead, DEFAULT_OG_IMAGE } from "@/lib/seo";

const HOMEPAGE_TITLE =
  "Alaska Fishing Lodge on Prince of Wales Island | Coffman Cove Lodging — Since 1984";
const HOMEPAGE_DESCRIPTION =
  "Affordable self-guided fishing lodge in Coffman Cove on Prince of Wales Island, Alaska. Cabins, boat rentals, and a live local fishing report. Family-run since 1984.";

// LodgingBusiness structured data. Latitude/longitude sourced from the
// Coffman Cove community coordinates (56.0147, -132.8339) — fact-check flag
// for Anthony: verify against the actual lodge property before publish.
const HOMEPAGE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  "@id": "https://coffmancovelodging.com/#lodging",
  name: "Coffman Cove Lodging",
  url: "https://coffmancovelodging.com",
  image: DEFAULT_OG_IMAGE,
  description: HOMEPAGE_DESCRIPTION,
  telephone: LODGE_PHONE_SCHEMA,
  email: LODGE_EMAIL,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "306 Harbor Ave",
    addressLocality: "Coffman Cove",
    addressRegion: "AK",
    postalCode: "99918",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 56.0147,
    longitude: -132.8339,
  },
  amenityFeature: [
    { "@type": "LocationFeatureSpecification", name: "Boat rentals", value: true },
    { "@type": "LocationFeatureSpecification", name: "Fish cleaning station", value: true },
    { "@type": "LocationFeatureSpecification", name: "Full kitchens", value: true },
    { "@type": "LocationFeatureSpecification", name: "Wi-Fi", value: true },
  ],
};

export const Route = createFileRoute("/")({
  head: () =>
    seoHead({
      path: "/",
      title: HOMEPAGE_TITLE,
      description: HOMEPAGE_DESCRIPTION,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(HOMEPAGE_JSON_LD),
        },
      ],
    }),
  component: Home,
});

// Tiny (32px wide) blurred JPEG derived from the poster frame — inlined so the
// hero paints something immediately, before any network request completes.
const HERO_LQIP =
  "data:image/jpeg;base64,/9j//gAQTGF2YzYyLjExLjEwMAD/2wBDAAgYGBwYHCEhISEhISckJygoKCcnJycoKCgrKyszMzMrKysoKCsrMDAzMzc5NzQ0MzQ5OTw8PEhIRUVUVFdnZ3z/xABvAAEBAQEAAAAAAAAAAAAAAAAHBgQFAQEBAQAAAAAAAAAAAAAAAAADBQQQAAIBAwMBBQkBAAAAAAAAAAECEQADIYESMQQzQkFxkXPBsbJy4TJhEyIRAAICAwEBAAAAAAAAAAAAAAEAAjEDQRESIf/AABEIABIAIAMBIgACEQADEQD/2gAMAwEAAhEDEQA/ANNnrR1ODyBWNsmR9qPktNuOzfgdyefOIPrVXbtdUOUkHiRDDWRGprSMoYssJ9XIA6DzbzKFYXP9YwARzpUM90BiVQD9HPhVndKKMqgYTkhWmNc1HMr3juG0+W0RPhFCZCf13wHgcHVv6xmW5ZUEgFWwDj0o9vMxvRJgXYAnAEcRV51va2fpb40fXu3PtvdUjbZ0wne1pEVF/sRtEYxAj8aO+9rSUO3OnyU+Sk4W/wD/2Q==";

function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Defer the network fetch until the browser is idle and the route has
    // committed. Prevents the video request from being aborted when
    // navigation ends early (e.g. during smoke tests / quick back-nav).
    let cancelled = false;
    const win = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
      };
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const start = () => {
      if (!cancelled) setShouldLoad(true);
    };

    if (typeof win.requestIdleCallback === "function") {
      idleId = win.requestIdleCallback(start, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(start, 300);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);

      // Cleanly tear down the media element so an in-flight fetch is
      // released by us instead of being reported as an aborted request.
      const el = videoRef.current;
      if (el) {
        try {
          el.pause();
          el.removeAttribute("src");
          el.load();
        } catch {
          /* noop */
        }
      }
    };
  }, []);

  return (
    <div className="relative h-[78vh] w-full overflow-hidden bg-[var(--color-navy-deep)]">
      {/* LQIP: paints instantly, blurred, before poster/video arrive. */}
      <img
        src={HERO_LQIP}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover scale-110 blur-xl"
      />
      {/* Poster: sharp still frame, hidden once video reports it can play. */}
      <img
        src={heroPoster.url}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        fetchPriority="high"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
          isReady ? "opacity-0" : "opacity-100"
        }`}
      />
      {/* Subtle shimmer while the video preload is still deferred. */}
      {!isReady && (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"
        />
      )}
      <video
        ref={videoRef}
        src={shouldLoad ? heroVideo.url : undefined}
        autoPlay
        loop
        muted
        playsInline
        preload={shouldLoad ? "auto" : "none"}
        poster={heroPoster.url}
        onCanPlay={() => setIsReady(true)}
        onPlaying={() => setIsReady(true)}
        aria-label="King salmon swimming underwater in Coffman Cove"
        className={`relative h-full w-full object-cover transition-opacity duration-500 ${
          isReady ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}

function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <HeroVideo />


        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/80" />
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto max-w-7xl w-full px-6 pb-20 text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">
            <p className="uppercase tracking-[0.3em] text-xs text-white/90">
              Prince of Wales Island · Southeast Alaska
            </p>
            <h1 className="font-display text-4xl md:text-6xl mt-4 max-w-4xl text-balance leading-[1.05] text-white">
              Affordable Alaska Fishing on Prince of Wales Island <span className="italic text-white">— Since 1984</span>
            </h1>
            <p className="mt-5 max-w-xl text-base md:text-lg text-white/95">
              Self-guided salmon and halibut out of Coffman Cove — cabins, boat rentals, and a live local fishing report.{" "}
              <Link to="/fishing" className="underline decoration-[var(--color-ember)] underline-offset-4 hover:text-[var(--color-ember)]">
                See what you'll be fishing for →
              </Link>
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="rounded-md bg-[var(--color-ember)] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
              >
                Book Your Trip
              </Link>
              <Link
                to="/fishing"
                className="rounded-md border border-white/30 px-5 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-white/10"
              >
                Fishing Overview
              </Link>
              <Link
                to="/fishing-report"
                className="rounded-md border border-white/30 px-5 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-white/10"
              >
                Live Fishing Report
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Halibut regulation one-liner — full self-guided pitch on /fishing. */}
      <section className="bg-[var(--color-ember)] text-[var(--color-ember-foreground)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center text-sm md:text-base font-semibold">
          <span>
            Self-guided = 2 halibut/day, any size. Charter boats can't say
            that.
          </span>
          <Link
            to="/fishing"
            hash="halibut-advantage"
            className="underline underline-offset-4 hover:opacity-90"
          >
            See the 2026 rules →
          </Link>
        </div>
      </section>

      {/* Welcome */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-24 text-center">
        <p className="uppercase tracking-[0.3em] text-xs text-[var(--color-ember)]">Welcome</p>
        <h2 className="font-display text-4xl md:text-5xl mt-3 text-balance">
          Over 40 years of family tradition in Southeast Alaska.
        </h2>
        <p className="mt-6 text-lg text-muted-foreground max-w-3xl mx-auto">
          Welcome to Coffman Cove Lodging, where over 40 years of family tradition meet the
          unforgettable beauty of Southeast Alaska. Family-owned and operated by Travis, Heather,
          and Hailey Hedges, we take pride in offering more than just a place to stay — we provide
          the local knowledge, comfortable accommodations, and genuine hospitality that have kept
          guests returning year after year. Whether you're chasing trophy King Salmon, giant
          Halibut, or simply looking to experience the peace and quiet that only Alaska can offer,
          you'll quickly discover why so many of our guests become lifelong friends. Come
          experience the Hedges family tradition and see for yourself why, around here, it's all
          about the fun.
        </p>
      </section>

      {/* Accommodation cards */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-24">
        <div className="grid gap-6 md:grid-cols-3">
          <Card
            img={lodgeImg.url}
            imgAlt="Coffman Cove Lodging guest cabin exterior with covered porch overlooking the harbor in Southeast Alaska"
            kicker="Rooms & Units"
            title="Lodging"
            body="Homestyle breakfast & dinner, sack lunch, and washer/dryer access included. Additional services available."
            to="/accommodations"
          />
          <Card
            img={kayakImg.url}
            imgSrcSet={`${kayakImg700.url} 700w, ${kayakImg.url} 1100w, ${kayakImg1600.url} 1600w`}
            imgSizes="(min-width: 768px) 33vw, 100vw"
            imgAlt="Hewescraft aluminum fishing skiff moored at the Coffman Cove dock, rigged with downriggers, GPS/sonar and VHF radio for self-guided salmon and halibut fishing"
            kicker="Rentals"
            title="Kayaks, skiffs & 22' boats"
            body="Boats include downriggers, GPS/Sonar, VHF radio and the gear list to actually catch fish."
            to="/accommodations"
          />
          <Card
            img={kingImg.url}
            imgAlt="Angler holding a large king salmon caught on a self-guided fishing trip out of Coffman Cove, Prince of Wales Island, Alaska"
            kicker="Explore Southeast Alaska"
            title="6 days of exciting fishing!"
            body="7 nights, lodging, meals, skiff/boat, rods, reels, filleting, vacuum sealing, freezing, and one fish box home."
            to="/contact"
          />

        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 grid gap-10 md:grid-cols-3">
          {[
            
            { icon: Fish, t: "Kings & Halibut", d: "World-class waters at your doorstep." },
            { icon: Compass, t: "Self-guided", d: "Charts, gear, and local intel — you run the day." },
            { icon: Waves, t: "Live conditions", d: "Tides, weather, and spot suggestions, daily." },
          ].map((f) => (
            <div key={f.t} className="flex flex-col gap-3">
              <f.icon className="text-[var(--color-ember)]" size={28} />
              <p className="font-display text-xl text-white">{f.t}</p>
              <p className="text-sm text-white/80">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-24 text-center">
        <p className="font-display text-2xl md:text-3xl text-balance leading-snug">
          "Jerry and Travis Hedges have a great little spot where an average angler can come fish
          for a week, rent their own boat stocked with the bait and gear, and fish the inland
          passage without the expense of a charter."
        </p>
        <p className="mt-6 uppercase tracking-widest text-xs text-muted-foreground">
          — Trip Advisor review
        </p>
      </section>
    </div>
  );
}

function Card({
  img, imgSrcSet, imgSizes, imgAlt, kicker, title, body, to,
}: { img: string; imgSrcSet?: string; imgSizes?: string; imgAlt?: string; kicker: string; title: string; body: string; to: string }) {
  return (
    <Link
      to={to}
      aria-label={`${title} — ${kicker}`}
      className="group rounded-lg overflow-hidden bg-card border border-border hover:border-[var(--color-ember)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ember)] focus-visible:ring-offset-2"
    >
      <div className="aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={img}
          srcSet={imgSrcSet}
          sizes={imgSizes}
          alt={imgAlt ?? title}
          loading="lazy"
          decoding="async"
          width={1280}
          height={900}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-6">
        <p className="uppercase tracking-[0.2em] text-[11px] text-[var(--color-ember)]">{kicker}</p>
        <h3 className="font-display text-2xl mt-2">{title}</h3>
        <p className="mt-3 text-sm text-muted-foreground">{body}</p>
        <p className="mt-4 text-sm font-semibold text-primary group-hover:text-[var(--color-ember)]">
          Learn more →
        </p>
      </div>
    </Link>
  );
}
